// General Info — three tabs:
//   Company Information  the agency's own numbers (NPI, Medicaid ID, EIN, …)
//   Facilities           every hospital, practice and referrer we work with
//   Physicians           every doctor, with NPI, where they work, phone, fax
// plus a Review tab for coordinators: physicians typed on forms that still
// need matching to the directory. See lib/directory.js for the rules.
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { format } from 'date-fns';
import { ROLE_LEVELS } from '../components/RoleGuard';
import { countOpenReviews } from '../lib/directory';
import FacilitiesTab from '../components/directory/FacilitiesTab';
import PhysiciansTab from '../components/directory/PhysiciansTab';
import ReviewQueueTab from '../components/directory/ReviewQueueTab';

const CATEGORIES = ['Company', 'Billing', 'Clinical', 'Contacts', 'Other'];

const catStyle = (cat) => ({
  Company:  { bg:'var(--info-bg)', color:'var(--info)' },
  Billing:  { bg:'var(--info-bg)', color:'var(--info)' },
  Clinical: { bg:'var(--warn-bg)', color:'var(--warn)' },
  Contacts: { bg:'var(--success-bg)', color:'var(--success)' },
  Other:    { bg:'var(--bg2)', color:'var(--text3)' },
}[cat] || { bg:'var(--accent-bg)', color:'var(--text3)' });

const emptyRow = () => ({ id: crypto.randomUUID(), label: '', category: 'Company', value: '', comments: '', isNew: true });

const TABS = [
  ['company',    'Company Information'],
  ['facilities', 'Facilities'],
  ['physicians', 'Physicians'],
  ['review',     'Review'],
];

export default function GeneralInfo() {
  const { profile } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === params.get('tab')) ? params.get('tab') : 'company';
  const setTab = (k) => setParams(k === 'company' ? {} : { tab: k }, { replace: true });
  const canEdit = (ROLE_LEVELS[profile?.role] || 0) >= ROLE_LEVELS.coordinator;
  const [reviewCount, setReviewCount] = useState(null);
  useEffect(() => { countOpenReviews().then(({ count }) => setReviewCount(count ?? 0)); }, [tab]);

  return (
    <div>
      <div style={{ display:'flex', gap:2, borderBottom:'1px solid var(--border2)', marginBottom:16, overflowX:'auto' }} role="tablist">
        {TABS.map(([k, labelText]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
            style={{ padding:'8px 14px', fontSize:13.5, fontWeight:500, fontFamily:'inherit', cursor:'pointer', whiteSpace:'nowrap', background:'none', border:'none',
              borderBottom:`2px solid ${tab === k ? 'var(--caera-copper)' : 'transparent'}`, color: tab === k ? 'var(--text)' : 'var(--text3)', marginBottom:-1 }}>
            {labelText}
            {k === 'review' && reviewCount > 0 && (
              <span style={{ marginLeft:6, fontSize:11, fontWeight:600, padding:'0 6px', borderRadius:999, background:'var(--warn-bg)', color:'var(--warn)', border:'1px solid var(--warn-border)' }}>{reviewCount}</span>
            )}
          </button>
        ))}
      </div>
      {tab === 'company'    && <CompanyInfoTab />}
      {tab === 'facilities' && <FacilitiesTab canEdit={canEdit} />}
      {tab === 'physicians' && <PhysiciansTab canEdit={canEdit} openId={params.get('physician')} />}
      {tab === 'review'     && <ReviewQueueTab canEdit={canEdit} onCountChange={setReviewCount} />}
    </div>
  );
}

function CompanyInfoTab() {
  const { profile } = useAuth();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [success, setSuccess] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    supabase.from('company_info').select('*').order('category').order('label')
      .then(({ data }) => { setRows(data || []); setLoading(false); });
  }, []);

  const update = (id, field, val) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: val, _changed: true } : r));
    setDirty(true);
  };

  const addRow = () => {
    setRows(prev => [...prev, emptyRow()]);
    setDirty(true);
  };

  const deleteRow = async (row) => {
    if (!row.isNew) await supabase.from('company_info').delete().eq('id', row.id);
    setRows(prev => prev.filter(r => r.id !== row.id));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setSuccess('');
    const toUpsert = rows
      .filter(r => r.label)
      .map(r => ({
        id: r.id,
        label: r.label,
        category: r.category,
        value: r.value || '',
        comments: r.comments || '',
        updated_by: profile?.full_name || '',
        updated_at: new Date().toISOString(),
      }));

    if (toUpsert.length > 0) {
      const { error } = await supabase.from('company_info').upsert(toUpsert, { onConflict: 'id' });
      if (error) {
        setSaving(false);
        // `label` is unique: every reader looks a value up by it, so two rows
        // sharing one would silently shadow each other.
        const duplicate = /company_info_label_key|duplicate key/i.test(error.message || '');
        setSuccess('❌ Save failed: ' + (duplicate
          ? 'two rows have the same label. Each label can only appear once — rename or remove one.'
          : error.message));
        setTimeout(() => setSuccess(''), 6000);
        return;
      }
      const { data: fresh } = await supabase.from('company_info').select('*').order('category').order('label');
      setRows(fresh || []);
    }
    setLastSaved({ time: new Date(), by: profile?.full_name || 'unknown' });
    setDirty(false);
    setSaving(false);
    setSuccess('Changes saved');
    setTimeout(() => setSuccess(''), 3000);
  };

  const filtered = filter === 'All' ? rows : rows.filter(r => r.category === filter);

  return (
    <div>
      {success && (
        <div style={{ background: success.startsWith('❌') ? 'var(--danger-bg)' : 'var(--success-bg)', border: `1px solid ${success.startsWith('❌') ? 'var(--danger-border)' : 'var(--success-border)'}`, borderRadius:8, padding:'10px 16px', marginBottom:14, color: success.startsWith('❌') ? 'var(--danger)' : 'var(--success)', fontSize:13 }}>
          {success.startsWith('❌') ? success : `✓ ${success}`}
        </div>
      )}

      {/* Category filters */}
      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:14 }}>
        {['All', ...CATEGORIES].map(cat => (
          <button
            key={cat}
            onClick={() => setFilter(cat)}
            style={{ padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:500, cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s', border:'1px solid', background:filter===cat?'var(--navy)':'transparent', color:filter===cat?'#fff':'var(--text2)', borderColor:filter===cat?'var(--text)':'var(--border)' }}
          >{cat}</button>
        ))}
      </div>

      <div className="card" style={{ border:'0.5px solid var(--info-border)' }}>
        <div className="card-header" style={{ background:'var(--info-bg)', borderBottom:'1px solid var(--info-border)', flexWrap:'wrap', gap:8 }}>
          <div className="card-title" style={{ color:'var(--info)' }}>General Information</div>
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-outline btn-sm" onClick={addRow} style={{ color:'var(--info)', borderColor:'var(--info-border)' }}>+ Add Row</button>
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
              {saving ? <span className="loading-spinner" style={{ borderTopColor:'#fff', width:14, height:14 }} /> : null}
              {saving ? ' Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="empty-state"><div className="loading-spinner" style={{ margin:'0 auto' }} /></div>
        ) : (
          <>
            {/* Desktop table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width:'100%', tableLayout:'fixed' }}>
                <thead>
                  <tr>
                    <th style={{ width:'20%', whiteSpace:'nowrap' }}>Information</th>
                    <th style={{ width:'12%', whiteSpace:'nowrap' }}>Category</th>
                    <th style={{ width:'38%', whiteSpace:'nowrap' }}>Description / Value</th>
                    <th style={{ width:'26%', whiteSpace:'nowrap' }}>Comments</th>
                    <th style={{ width:'4%' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign:'center', padding:32, color:'var(--text3)', fontSize:13 }}>No entries yet. Click + Add Row to get started.</td></tr>
                  ) : filtered.map(row => {
                    const cs = catStyle(row.category);
                    const autoResize = el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } };
                    return (
                      <tr key={row.id} style={{ transition:'background 0.1s', verticalAlign:'top' }}
                        onMouseEnter={e => e.currentTarget.style.background='var(--accent-bg)'}
                        onMouseLeave={e => e.currentTarget.style.background='transparent'}
                      >
                        <td style={{ padding:0 }}>
                          <textarea
                            ref={autoResize}
                            value={row.label}
                            onChange={e => { update(row.id, 'label', e.target.value); autoResize(e.target); }}
                            placeholder="Label..."
                            rows={1}
                            style={{ width:'100%', border:'none', background:'transparent', fontFamily:'inherit', fontSize:13, fontWeight:500, color:'var(--text)', padding:'10px 16px', outline:'none', resize:'none', overflow:'hidden', lineHeight:1.5, boxSizing:'border-box', display:'block' }}
                            onFocus={e => e.target.style.background='var(--info-bg)'}
                            onBlur={e => e.target.style.background='transparent'}
                          />
                        </td>
                        <td style={{ padding:'10px 12px' }}>
                          <select
                            value={row.category}
                            onChange={e => update(row.id, 'category', e.target.value)}
                            style={{ fontSize:11, fontWeight:500, padding:'3px 8px', borderRadius:20, border:'none', cursor:'pointer', fontFamily:'inherit', background:cs.bg, color:cs.color }}
                          >
                            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                          </select>
                        </td>
                        <td style={{ padding:0 }}>
                          <textarea
                            ref={autoResize}
                            value={row.value}
                            onChange={e => { update(row.id, 'value', e.target.value); autoResize(e.target); }}
                            placeholder="Value..."
                            rows={1}
                            style={{ width:'100%', border:'none', background:'transparent', fontFamily:'inherit', fontSize:13, color:'var(--text)', padding:'10px 16px', outline:'none', resize:'none', overflow:'hidden', lineHeight:1.5, boxSizing:'border-box', display:'block', wordBreak:'break-word' }}
                            onFocus={e => e.target.style.background='var(--info-bg)'}
                            onBlur={e => e.target.style.background='transparent'}
                          />
                        </td>
                        <td style={{ padding:0 }}>
                          <textarea
                            ref={autoResize}
                            value={row.comments}
                            onChange={e => { update(row.id, 'comments', e.target.value); autoResize(e.target); }}
                            placeholder="Comments..."
                            rows={1}
                            style={{ width:'100%', border:'none', background:'transparent', fontFamily:'inherit', fontSize:12, color:'var(--text2)', padding:'10px 16px', outline:'none', resize:'none', overflow:'hidden', lineHeight:1.5, boxSizing:'border-box', display:'block', wordBreak:'break-word' }}
                            onFocus={e => e.target.style.background='var(--info-bg)'}
                            onBlur={e => e.target.style.background='transparent'}
                          />
                        </td>
                        <td style={{ padding:'10px 8px', textAlign:'center' }}>
                          <button
                            onClick={() => deleteRow(row)}
                            style={{ background:'none', border:'none', color:'var(--danger)', fontSize:18, cursor:'pointer', opacity:0.5, lineHeight:1, padding:'0 4px' }}
                            onMouseEnter={e => e.target.style.opacity=1}
                            onMouseLeave={e => e.target.style.opacity=0.5}
                          >×</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile card view */}
            <div style={{ display:'none' }} className="mobile-gi-cards">
              {filtered.map(row => {
                const cs = catStyle(row.category);
                return (
                  <div key={row.id} style={{ padding:14, borderBottom:'0.5px solid var(--border)' }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                      <input
                        value={row.label}
                        onChange={e => update(row.id, 'label', e.target.value)}
                        placeholder="Label..."
                        style={{ flex:1, border:'none', background:'transparent', fontFamily:'inherit', fontSize:14, fontWeight:500, color:'var(--text)', outline:'none', minHeight:44 }}
                      />
                      <button onClick={() => deleteRow(row)} style={{ background:'none', border:'none', color:'var(--danger)', fontSize:20, cursor:'pointer', padding:'0 4px' }}>×</button>
                    </div>
                    <select
                      value={row.category}
                      onChange={e => update(row.id, 'category', e.target.value)}
                      style={{ fontSize:12, fontWeight:500, padding:'4px 10px', borderRadius:20, border:'none', cursor:'pointer', fontFamily:'inherit', background:cs.bg, color:cs.color, marginBottom:8 }}
                    >
                      {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                    <div style={{ marginBottom:6 }}>
                      <div style={{ fontSize:11, color:'var(--text3)', marginBottom:3 }}>Value</div>
                      <input
                        value={row.value}
                        onChange={e => update(row.id, 'value', e.target.value)}
                        placeholder="Value..."
                        style={{ width:'100%', border:'0.5px solid var(--border)', borderRadius:6, padding:'8px 10px', fontFamily:'inherit', fontSize:14, color:'var(--text)', outline:'none', boxSizing:'border-box', minHeight:44 }}
                      />
                    </div>
                    <div>
                      <div style={{ fontSize:11, color:'var(--text3)', marginBottom:3 }}>Comments</div>
                      <input
                        value={row.comments}
                        onChange={e => update(row.id, 'comments', e.target.value)}
                        placeholder="Comments..."
                        style={{ width:'100%', border:'0.5px solid var(--border)', borderRadius:6, padding:'8px 10px', fontFamily:'inherit', fontSize:14, color:'var(--text2)', outline:'none', boxSizing:'border-box', minHeight:44 }}
                      />
                    </div>
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <div style={{ textAlign:'center', padding:32, color:'var(--text3)', fontSize:13 }}>No entries yet. Tap + Add Row to get started.</div>
              )}
            </div>
          </>
        )}
      </div>

      {lastSaved && (
        <div style={{ fontSize:11, color:'var(--text3)', textAlign:'right', marginTop:6 }}>
          Last saved: {format(lastSaved.time, 'MMM d, yyyy')} at {format(lastSaved.time, 'h:mm a')} · by {lastSaved.by}
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .table-wrapper { display: none !important; }
          .mobile-gi-cards { display: block !important; }
        }
      `}</style>
    </div>
  );
}
